import React, { useState, useEffect } from 'react';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Button from '../../../components/ui/Button';
import ColorPicker from '../../../components/ui/ColorPicker';
import Icon from '../../../components/AppIcon';
import FileUpload from '../../../components/ui/FileUpload';
import { uploadImageAndGetUrl, getSignedImageUrl, deleteImage } from '../../../utils/imageUpload';
import { formatSocialMediaUrl, validateSocialMediaHandle } from '../../../utils/socialMediaFormatter';
import { worksService } from '../../../services/artistContentService';
import { useAuth } from '../../../contexts/AuthContext';

const ProfileSection = ({ artistData, onUpdate, isActive }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    website: '',
    highlightColor: '#3B82F1', // Default blue color for mobile app
    backgroundColor: '#000000', // Default black color for mobile app background
    secondaryColor: '#6366F1', // Default secondary color for mobile app
    artist_logo_url: '', // Add artist logo URL field
    instagram: '',
    twitter: '',
    facebook: '',
    imdb: '',
    linkedin: '',
    soundcloud: '', // Add SoundCloud entry
    snapchat: '', // Add Snapchat entry
    artist_type: 'musician', // Default artist type
    // Add artist music platform URLs
    artistSpotify: '',
    artistAppleMusic: '',
    artistYouTubeMusic: '',
    artistTidal: ''
  });

  const [errors, setErrors] = useState({});
  const [isEditing, setIsEditing] = useState(false);
  const [saveStatus, setSaveStatus] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [logoUpload, setLogoUpload] = useState({
    uploading: false,
    progress: null,
    error: null,
    currentUrl: null
  });

  // Artist type options matching the database enum
  const artistTypeOptions = [
    { value: 'musician', label: 'Musician' },
    { value: 'visual', label: 'Visual Artist' },
    { value: 'director', label: 'Director' },
    { value: 'actor', label: 'Actor' },
    { value: 'writer', label: 'Writer' }
  ];

  // Import worksService to update work page config
  const { user } = useAuth();

  // Initialize form data from artistData
  useEffect(() => {
    if (artistData?.profile) {
      const socialLinks = artistData?.profile?.social_links || {};
      const settings = artistData?.profile?.settings || {};
      setFormData({
        name: artistData?.profile?.name || '',
        email: artistData?.profile?.email || '',
        phone: artistData?.profile?.phone || '',
        website: artistData?.profile?.website || '',
        highlightColor: settings?.highlightColor || artistData?.profile?.highlight_color || '#3B82F1', // Load from settings or column
        backgroundColor: settings?.backgroundColor || artistData?.profile?.background_color || '#000000', // Load background color from settings or column with black default
        secondaryColor: settings?.secondaryColor || artistData?.profile?.secondary_color || '#6366F1', // Load secondary color from settings or column
        artist_logo_url: artistData?.profile?.artist_logo_url || '', // Load artist logo URL
        instagram: artistData?.profile?.instagram || '',
        twitter: artistData?.profile?.twitter || '',
        facebook: artistData?.profile?.facebook || '',
        imdb: artistData?.profile?.imdb || '',
        linkedin: artistData?.profile?.linkedin || '',
        soundcloud: socialLinks?.soundcloud || '', // Load SoundCloud from social_links
        snapchat: socialLinks?.snapchat || '', // Load Snapchat from social_links
        artist_type: artistData?.profile?.artist_type || 'musician',
        // Extract artist music URLs from social_links
        artistSpotify: socialLinks?.artistSpotify || '',
        artistAppleMusic: socialLinks?.artistAppleMusic || '',
        artistYouTubeMusic: socialLinks?.artistYouTubeMusic || '',
        artistTidal: socialLinks?.artistTidal || ''
      });
      
      // Load current logo URL if available
      if (artistData?.profile?.artist_logo_url) {
        loadLogoUrl(artistData?.profile?.artist_logo_url);
      }
    }
  }, [artistData?.profile]);

  // Load signed URL for current logo
  const loadLogoUrl = async (logoPath) => {
    if (!logoPath) {
      setLogoUpload(prev => ({ ...prev, currentUrl: null }));
      return;
    }

    try {
      const result = await getSignedImageUrl('artist-profile-images', logoPath, 7200);
      if (result?.data?.signedUrl) {
        setLogoUpload(prev => ({ 
          ...prev, 
          currentUrl: result?.data?.signedUrl,
          error: null
        }));
      } else {
        setLogoUpload(prev => ({ 
          ...prev, 
          currentUrl: null,
          error: result?.error || 'Failed to load logo image'
        }));
      }
    } catch (error) {
      setLogoUpload(prev => ({ 
        ...prev, 
        currentUrl: null,
        error: 'Failed to load logo image'
      }));
    }
  };

  // Handle logo upload
  const handleLogoUpload = async (file) => {
    if (!file) return;

    setLogoUpload(prev => ({
      ...prev,
      uploading: true,
      progress: 0,
      error: null
    }));

    try {
      // Delete existing logo if present
      if (formData?.artist_logo_url) {
        await deleteImage('artist-profile-images', formData?.artist_logo_url);
      }

      // Upload new logo
      const result = await uploadImageAndGetUrl(file, artistData?.id, 'profile');
      
      if (result?.error) {
        setLogoUpload(prev => ({
          ...prev,
          uploading: false,
          progress: null,
          error: result?.error
        }));
        return;
      }

      // Update form data with new logo path
      const newLogoPath = result?.data?.path;
      setFormData(prev => ({
        ...prev,
        artist_logo_url: newLogoPath
      }));

      // Set current URL for immediate display
      setLogoUpload(prev => ({
        ...prev,
        uploading: false,
        progress: null,
        error: null,
        currentUrl: result?.data?.url
      }));

      // Auto-save if not in edit mode
      if (!isEditing) {
        await handleSave();
      }

    } catch (error) {
      setLogoUpload(prev => ({
        ...prev,
        uploading: false,
        progress: null,
        error: 'Failed to upload logo. Please try again.'
      }));
    }
  };

  // Handle logo removal
  const handleLogoRemove = async () => {
    if (!formData?.artist_logo_url) return;

    try {
      // Delete from storage
      await deleteImage('artist-profile-images', formData?.artist_logo_url);
      
      // Update form data
      setFormData(prev => ({
        ...prev,
        artist_logo_url: ''
      }));

      // Clear logo display
      setLogoUpload(prev => ({
        ...prev,
        currentUrl: null,
        error: null
      }));

      // Auto-save if not in edit mode
      if (!isEditing) {
        await handleSave();
      }

    } catch (error) {
      setLogoUpload(prev => ({
        ...prev,
        error: 'Failed to remove logo. Please try again.'
      }));
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors?.[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
    
    // Clear save status when editing
    if (saveStatus) {
      setSaveStatus(null);
    }

    // NEW: Auto-update work page configuration when artist_type changes
    if (field === 'artist_type') {
      handleArtistTypeChange(value);
    }
  };

  // NEW: Function to automatically set work page defaults based on artist type
  const handleArtistTypeChange = async (artistType) => {
    if (!user?.id || !artistType) return;

    // Define defaults based on artist type as per requirements
    let workPageDefaults = {};

    switch (artistType) {
      case 'musician':
        workPageDefaults = {
          work_page_type: 'streaming_platforms', // "Artist Streaming Platforms"
          work_page_icon: 'music',               // "Music"
          work_page_title: 'music'               // "Music"
        };
        break;
      case 'visual':
        workPageDefaults = {
          work_page_type: 'individual_works',    // "Individual Works Below"
          work_page_icon: 'art',                 // "Art" 
          work_page_title: 'art'                 // "Art"
        };
        break;
      case 'director': case 'actor':
        workPageDefaults = {
          work_page_type: 'individual_works',    // "Individual Works Below"
          work_page_icon: 'film',                // "Film"
          work_page_title: 'films'               // "Films"
        };
        break;
      case 'writer':
        workPageDefaults = {
          work_page_type: 'individual_works',    // "Individual Works Below"
          work_page_icon: 'book',                // "Book"
          work_page_title: 'writings'            // "Writings"
        };
        break;
      default:
        // No defaults for unknown types
        return;
    }

    try {
      // Update work page configuration in the background
      const result = await worksService?.updateWorkPageConfig?.(user?.id, {
        page_type: workPageDefaults?.work_page_type,
        page_icon: workPageDefaults?.work_page_icon,
        page_title: workPageDefaults?.work_page_title
      });

      if (result?.error) {
        console.warn('Failed to auto-update work page config:', result?.error);
      }
    } catch (error) {
      console.warn('Error auto-updating work page config:', error);
    }
  };

  // New function to handle social media input with auto-formatting
  const handleSocialMediaChange = (platform, value) => {
    // Update form data with raw input first
    setFormData(prev => ({
      ...prev,
      [platform]: value
    }));
    
    // Clear error when user starts typing
    if (errors?.[platform]) {
      setErrors(prev => ({
        ...prev,
        [platform]: ''
      }));
    }
    
    // Clear save status when editing
    if (saveStatus) {
      setSaveStatus(null);
    }
  };

  // New function to handle social media blur (auto-format on blur)
  const handleSocialMediaBlur = (platform, value) => {
    if (!value || !value?.trim()) return;
    
    // Validate the input
    const validation = validateSocialMediaHandle(platform, value);
    if (!validation?.isValid) {
      setErrors(prev => ({
        ...prev,
        [platform]: validation?.message
      }));
      return;
    }
    
    // Auto-format the URL
    const formattedUrl = formatSocialMediaUrl(platform, value);
    
    // Update form data with formatted URL
    setFormData(prev => ({
      ...prev,
      [platform]: formattedUrl
    }));
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData?.name?.trim()) {
      newErrors.name = 'Artist name is required';
    }
    
    if (!formData?.email?.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/?.test(formData?.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    
    if (formData?.phone && !/^\+?[\d\s\-\(\)]+$/?.test(formData?.phone)) {
      newErrors.phone = 'Please enter a valid phone number';
    }
    
    if (formData?.website && !/^https?:\/\/.+/?.test(formData?.website)) {
      newErrors.website = 'Please enter a valid website URL (include http:// or https://)';
    }

    if (!formData?.artist_type) {
      newErrors.artist_type = 'Please select an artist type';
    }

    // Validate social media handles
    const socialPlatforms = ['instagram', 'twitter', 'facebook', 'soundcloud', 'snapchat', 'linkedin', 'imdb'];
    socialPlatforms?.forEach(platform => {
      if (formData?.[platform]) {
        const validation = validateSocialMediaHandle(platform, formData?.[platform]);
        if (!validation?.isValid) {
          newErrors[platform] = validation?.message;
        }
      }
    });

    // Validate artist music URLs
    const urlPattern = /^https?:\/\/.+/;
    if (formData?.artistSpotify && !urlPattern?.test(formData?.artistSpotify)) {
      newErrors.artistSpotify = 'Please enter a valid Spotify URL';
    }
    if (formData?.artistAppleMusic && !urlPattern?.test(formData?.artistAppleMusic)) {
      newErrors.artistAppleMusic = 'Please enter a valid Apple Music URL';
    }
    if (formData?.artistYouTubeMusic && !urlPattern?.test(formData?.artistYouTubeMusic)) {
      newErrors.artistYouTubeMusic = 'Please enter a valid YouTube Music URL';
    }
    if (formData?.artistTidal && !urlPattern?.test(formData?.artistTidal)) {
      newErrors.artistTidal = 'Please enter a valid Tidal URL';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }
    
    setIsSaving(true);
    setSaveStatus(null);
    
    try {
      // Prepare profile data with highlight color and background color in settings
      const profileData = {
        ...formData,
        highlight_color: formData?.highlightColor, // Save to database column for mobile app
        background_color: formData?.backgroundColor, // Save background color to database column for mobile app
        secondary_color: formData?.secondaryColor, // Save secondary color to database column for mobile app
        settings: {
          highlightColor: formData?.highlightColor,
          backgroundColor: formData?.backgroundColor,
          secondaryColor: formData?.secondaryColor
        },
        artist_logo_url: formData?.artist_logo_url, // Save logo URL to database
        social_links: {
          instagram: formData?.instagram,
          twitter: formData?.twitter,
          facebook: formData?.facebook,
          imdb: formData?.imdb,
          linkedin: formData?.linkedin,
          soundcloud: formData?.soundcloud, // Add SoundCloud to social_links
          snapchat: formData?.snapchat, // Add Snapchat to social_links
          // Add artist music platform URLs
          artistSpotify: formData?.artistSpotify,
          artistAppleMusic: formData?.artistAppleMusic,
          artistYouTubeMusic: formData?.artistYouTubeMusic,
          artistTidal: formData?.artistTidal
        }
      };

      const result = await onUpdate('profile', profileData);
      
      if (result?.error) {
        setSaveStatus({
          type: 'error',
          message: result?.error?.message || 'Failed to save profile changes'
        });
      } else {
        setSaveStatus({
          type: 'success',
          message: 'Profile saved successfully!'
        });
        setIsEditing(false);
        
        // Auto-clear success message after 3 seconds
        setTimeout(() => {
          setSaveStatus(null);
        }, 3000);
      }
    } catch (error) {
      setSaveStatus({
        type: 'error',
        message: 'Failed to save profile changes. Please try again.'
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    // Reset form data to original artistData values
    if (artistData?.profile) {
      const socialLinks = artistData?.profile?.social_links || {};
      const settings = artistData?.profile?.settings || {};
      setFormData({
        name: artistData?.profile?.name || '',
        email: artistData?.profile?.email || '',
        phone: artistData?.profile?.phone || '',
        website: artistData?.profile?.website || '',
        highlightColor: settings?.highlightColor || artistData?.profile?.highlight_color || '#3B82F1', // Reset highlight color
        backgroundColor: settings?.backgroundColor || artistData?.profile?.background_color || '#000000', // Reset background color with black default
        secondaryColor: settings?.secondaryColor || artistData?.profile?.secondary_color || '#6366F1', // Reset secondary color
        artist_logo_url: artistData?.profile?.artist_logo_url || '', // Reset artist logo
        instagram: artistData?.profile?.instagram || '',
        twitter: artistData?.profile?.twitter || '',
        facebook: artistData?.profile?.facebook || '',
        imdb: artistData?.profile?.imdb || '',
        linkedin: artistData?.profile?.linkedin || '',
        soundcloud: socialLinks?.soundcloud || '', // Reset SoundCloud
        snapchat: socialLinks?.snapchat || '', // Reset Snapchat
        artist_type: artistData?.profile?.artist_type || 'musician',
        artistSpotify: socialLinks?.artistSpotify || '',
        artistAppleMusic: socialLinks?.artistAppleMusic || '',
        artistYouTubeMusic: socialLinks?.artistYouTubeMusic || '',
        artistTidal: socialLinks?.artistTidal || ''
      });
      
      // Reset logo display
      if (artistData?.profile?.artist_logo_url) {
        loadLogoUrl(artistData?.profile?.artist_logo_url);
      } else {
        setLogoUpload(prev => ({ ...prev, currentUrl: null, error: null }));
      }
    }
    setErrors({});
    setSaveStatus(null);
    setIsEditing(false);
  };

  if (!isActive) return null;

  return (
    <div className="bg-surface rounded-lg border border-border p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center">
            <Icon name="User" size={20} className="text-primary-foreground" />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-text-primary">Profile Information</h2>
            <p className="text-sm text-text-secondary">Manage your basic profile details and contact information</p>
          </div>
        </div>
        
        <div className="flex items-center space-x-2">
          {isEditing ? (
            <>
              <Button variant="outline" size="sm" onClick={handleCancel} disabled={isSaving}>
                Cancel
              </Button>
              <Button 
                variant="default" 
                size="sm" 
                onClick={handleSave}
                disabled={isSaving}
                iconName={isSaving ? "Loader2" : "Save"}
                iconPosition="left"
                iconSize={16}
              >
                {isSaving ? 'Saving...' : 'Save Changes'}
              </Button>
            </>
          ) : (
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => setIsEditing(true)}
              iconName="Edit"
              iconPosition="left"
              iconSize={16}
            >
              Edit Profile
            </Button>
          )}
        </div>
      </div>
      {/* Save Status Message */}
      {saveStatus && (
        <div className={`mb-6 p-4 rounded-lg flex items-center space-x-2 ${
          saveStatus?.type === 'success' ?'bg-success/10 text-success border border-success/20' :'bg-destructive/10 text-destructive border border-destructive/20'
        }`}>
          <Icon 
            name={saveStatus?.type === 'success' ? "CheckCircle" : "AlertCircle"} 
            size={16} 
          />
          <span className="text-sm font-medium">{saveStatus?.message}</span>
        </div>
      )}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Basic Information */}
        <div className="space-y-4">
          <h3 className="text-lg font-medium text-text-primary border-b border-border pb-2">
            Basic Information
          </h3>

          {/* Artist ID - Non-editable display */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-text-primary mb-2">
              Artist ID
            </label>
            <div className="px-3 py-2 bg-muted border border-border rounded-lg text-text-secondary text-sm font-mono">
              {artistData?.id || 'Loading...'}
            </div>
            <p className="text-xs text-text-secondary mt-1">
              Your unique artist identifier (read-only)
            </p>
          </div>
          
          <Input
            label="Artist Name"
            type="text"
            placeholder="Enter your artist name"
            value={formData?.name}
            onChange={(e) => handleInputChange('name', e?.target?.value)}
            error={errors?.name}
            required
            disabled={!isEditing}
            className="mb-4"
          />

          {/* Artist Type Dropdown */}
          <Select
            label="Artist Type"
            options={artistTypeOptions}
            value={formData?.artist_type}
            onChange={(value) => handleInputChange('artist_type', value)}
            placeholder="Select your artist type"
            error={errors?.artist_type}
            required
            disabled={!isEditing}
            className="mb-4"
          />
          
          <Input
            label="Email Address"
            type="email"
            placeholder="Enter your email address"
            value={formData?.email}
            onChange={(e) => handleInputChange('email', e?.target?.value)}
            error={errors?.email}
            required
            disabled={!isEditing}
            className="mb-4"
          />
          
          <Input
            label="Phone Number"
            type="tel"
            placeholder="Enter your phone number"
            value={formData?.phone}
            onChange={(e) => handleInputChange('phone', e?.target?.value)}
            error={errors?.phone}
            disabled={!isEditing}
            className="mb-4"
          />
          
          <Input
            label="Website"
            type="url"
            placeholder="https://yourwebsite.com"
            value={formData?.website}
            onChange={(e) => handleInputChange('website', e?.target?.value)}
            error={errors?.website}
            disabled={!isEditing}
            className="mb-4"
          />

          {/* Highlight Color Picker - Enhanced clickable preview */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-text-primary mb-2">
              Highlight Color
              {!isEditing && (
                <span className="text-xs text-text-secondary ml-2">(View Only)</span>
              )}
            </label>
            <div className="flex items-center space-x-3">
              {/* Clickable Color Preview - Now opens color picker */}
              <div className="flex flex-col items-center space-y-1">
                <ColorPicker
                  value={formData?.highlightColor}
                  onChange={(color) => handleInputChange('highlightColor', color)}
                  disabled={!isEditing}
                  showInput={false}
                  showPreview={true}
                  previewSize="lg"
                  className="ring-2 ring-offset-2 ring-transparent hover:ring-primary/20"
                />
                <span className="text-xs text-text-secondary font-medium">
                  Click to change
                </span>
              </div>
              
              {/* Color Input Field */}
              <div className="flex-1">
                <input
                  type="text"
                  value={formData?.highlightColor}
                  onChange={(e) => handleInputChange('highlightColor', e?.target?.value)}
                  disabled={!isEditing}
                  placeholder="#3B82F6"
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                />
                <p className="text-xs text-text-secondary mt-1">
                  This color will be used for buttons and links in your mobile app
                </p>
              </div>
            </div>
          </div>

          {/* Background Color Picker - Enhanced clickable preview */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-text-primary mb-2">
              Background Color
              {!isEditing && (
                <span className="text-xs text-text-secondary ml-2">(View Only)</span>
              )}
            </label>
            <div className="flex items-center space-x-3">
              {/* Clickable Color Preview - Now opens color picker */}
              <div className="flex flex-col items-center space-y-1">
                <ColorPicker
                  value={formData?.backgroundColor}
                  onChange={(color) => handleInputChange('backgroundColor', color)}
                  disabled={!isEditing}
                  showInput={false}
                  showPreview={true}
                  previewSize="lg"
                  className="ring-2 ring-offset-2 ring-transparent hover:ring-primary/20"
                />
                <span className="text-xs text-text-secondary font-medium">
                  Click to change
                </span>
              </div>
              
              {/* Color Input Field */}
              <div className="flex-1">
                <input
                  type="text"
                  value={formData?.backgroundColor}
                  onChange={(e) => handleInputChange('backgroundColor', e?.target?.value)}
                  disabled={!isEditing}
                  placeholder="#000000"
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                />
                <p className="text-xs text-text-secondary mt-1">
                  This color will be used as the main background in your mobile app
                </p>
              </div>
            </div>
          </div>

          {/* Secondary Color Picker */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-text-primary mb-2">
              Secondary Color
              {!isEditing && (
                <span className="text-xs text-text-secondary ml-2">(View Only)</span>
              )}
            </label>
            <div className="flex items-center space-x-3">
              {/* Clickable Color Preview */}
              <div className="flex flex-col items-center space-y-1">
                <ColorPicker
                  value={formData?.secondaryColor}
                  onChange={(color) => handleInputChange('secondaryColor', color)}
                  disabled={!isEditing}
                  showInput={false}
                  showPreview={true}
                  previewSize="lg"
                  className="ring-2 ring-offset-2 ring-transparent hover:ring-primary/20"
                />
                <span className="text-xs text-text-secondary font-medium">
                  Click to change
                </span>
              </div>
              
              {/* Color Input Field */}
              <div className="flex-1">
                <input
                  type="text"
                  value={formData?.secondaryColor}
                  onChange={(e) => handleInputChange('secondaryColor', e?.target?.value)}
                  disabled={!isEditing}
                  placeholder="#6366F1"
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                />
                <p className="text-xs text-text-secondary mt-1">
                  This color will be used as the secondary accent color in your mobile app
                </p>
              </div>
            </div>
          </div>

          {/* Artist Logo Upload - NEW */}
          <div className="mb-4">
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-text-primary">
                Artist Logo
              </label>
              {logoUpload?.currentUrl && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleLogoRemove}
                  disabled={!isEditing || logoUpload?.uploading}
                  iconName="Trash2"
                  iconSize={14}
                  className="text-destructive hover:text-destructive"
                >
                  Remove
                </Button>
              )}
            </div>

            {/* Current Logo Display */}
            {logoUpload?.currentUrl && (
              <div className="mb-3 p-3 border border-border rounded-lg bg-muted">
                <div className="flex items-center space-x-3">
                  <div className="w-16 h-16 bg-background rounded-lg border border-border flex items-center justify-center overflow-hidden">
                    <img 
                      src={logoUpload?.currentUrl} 
                      alt="Artist Logo"
                      className="max-w-full max-h-full object-contain"
                    />
                  </div>
                  <div className="flex-1">
                    <div className="text-sm font-medium text-text-primary">Current Logo</div>
                    <div className="text-xs text-text-secondary">
                      {formData?.artist_logo_url?.split('/')?.pop()?.split('_')?.slice(-1)?.[0] || 'artist-logo'}
                    </div>
                  </div>
                </div>
              </div>
            )}

            <FileUpload
              label={null}
              accept="image/png,image/svg+xml,.png,.svg"
              onFileSelect={handleLogoUpload}
              disabled={!isEditing || logoUpload?.uploading}
              error={logoUpload?.error}
              maxFileSize={5}
              allowedTypes={['image/png', 'image/svg+xml']}
              description="Upload your artist logo for mobile app branding"
              currentFileName={logoUpload?.currentUrl ? 'Logo uploaded' : null}
              uploadProgress={logoUpload?.progress}
              isUploading={logoUpload?.uploading}
              className="mb-4"
            />

            <p className="text-xs text-text-secondary">
              PNG or SVG format only. This logo will be displayed in your mobile app and promotional materials.
            </p>
          </div>
        </div>

        {/* Social Links & Artist Music URLs */}
        <div className="space-y-4">
          <h3 className="text-lg font-medium text-text-primary border-b border-border pb-2">
            Social Media & Professional Links
          </h3>
          <p className="text-xs text-text-secondary mb-4">
            Enter just your username (e.g., "ryanculvermusic") and we'll automatically format it to the proper URL
          </p>
          
          <Input
            label="Instagram Handle"
            type="text"
            placeholder="ryanculvermusic or @ryanculvermusic"
            value={formData?.instagram}
            onChange={(e) => handleSocialMediaChange('instagram', e?.target?.value)}
            onBlur={(e) => handleSocialMediaBlur('instagram', e?.target?.value)}
            error={errors?.instagram}
            disabled={!isEditing}
            className="mb-4"
            description={formData?.instagram && formData?.instagram?.startsWith('http') ? 
              `Preview: ${formData?.instagram}` : 
              'Links to: https://www.instagram.com/yourusername'
            }
          />
          
          <Input
            label="Twitter Handle"
            type="text"
            placeholder="ryanculvermusic or @ryanculvermusic"
            value={formData?.twitter}
            onChange={(e) => handleSocialMediaChange('twitter', e?.target?.value)}
            onBlur={(e) => handleSocialMediaBlur('twitter', e?.target?.value)}
            error={errors?.twitter}
            disabled={!isEditing}
            className="mb-4"
            description={formData?.twitter && formData?.twitter?.startsWith('http') ? 
              `Preview: ${formData?.twitter}` : 
              'Links to: https://twitter.com/yourusername'
            }
          />
          
          <Input
            label="Facebook Page"
            type="text"
            placeholder="ryanculvermusic"
            value={formData?.facebook}
            onChange={(e) => handleSocialMediaChange('facebook', e?.target?.value)}
            onBlur={(e) => handleSocialMediaBlur('facebook', e?.target?.value)}
            error={errors?.facebook}
            disabled={!isEditing}
            className="mb-4"
            description={formData?.facebook && formData?.facebook?.startsWith('http') ? 
              `Preview: ${formData?.facebook}` : 
              'Links to: https://www.facebook.com/yourpage'
            }
          />

          <Input
            label="SoundCloud Profile"
            type="text"
            placeholder="ryanculvermusic"
            value={formData?.soundcloud}
            onChange={(e) => handleSocialMediaChange('soundcloud', e?.target?.value)}
            onBlur={(e) => handleSocialMediaBlur('soundcloud', e?.target?.value)}
            error={errors?.soundcloud}
            disabled={!isEditing}
            className="mb-4"
            description={formData?.soundcloud && formData?.soundcloud?.startsWith('http') ? 
              `Preview: ${formData?.soundcloud}` : 
              'Links to: https://soundcloud.com/yourusername'
            }
          />

          <Input
            label="Snapchat Username"
            type="text"
            placeholder="ryanculvermusic"
            value={formData?.snapchat}
            onChange={(e) => handleSocialMediaChange('snapchat', e?.target?.value)}
            onBlur={(e) => handleSocialMediaBlur('snapchat', e?.target?.value)}
            error={errors?.snapchat}
            disabled={!isEditing}
            className="mb-4"
            description={formData?.snapchat && formData?.snapchat?.startsWith('http') ? 
              `Preview: ${formData?.snapchat}` : 
              'Links to: https://www.snapchat.com/add/yourusername'
            }
          />
          
          <Input
            label="IMDB Profile"
            type="text"
            placeholder="nm1234567 or Your Name"
            value={formData?.imdb}
            onChange={(e) => handleSocialMediaChange('imdb', e?.target?.value)}
            onBlur={(e) => handleSocialMediaBlur('imdb', e?.target?.value)}
            error={errors?.imdb}
            disabled={!isEditing}
            className="mb-4"
            description={formData?.imdb && formData?.imdb?.startsWith('http') ? 
              `Preview: ${formData?.imdb}` : 
              'Links to: IMDB ID or search by name'
            }
          />
          
          <Input
            label="LinkedIn Profile"
            type="text"
            placeholder="ryanculvermusic"
            value={formData?.linkedin}
            onChange={(e) => handleSocialMediaChange('linkedin', e?.target?.value)}
            onBlur={(e) => handleSocialMediaBlur('linkedin', e?.target?.value)}
            error={errors?.linkedin}
            disabled={!isEditing}
            className="mb-4"
            description={formData?.linkedin && formData?.linkedin?.startsWith('http') ? 
              `Preview: ${formData?.linkedin}` : 
              'Links to: https://www.linkedin.com/in/yourusername'
            }
          />

          {/* Artist Music Platform URLs Section */}
          <h4 className="text-md font-medium text-text-primary border-b border-border pb-2 mt-6">
            Artist Music Platform URLs
          </h4>
          <p className="text-xs text-text-secondary mb-4">
            These are your main artist profile URLs (different from individual work URLs)
          </p>
          
          <Input
            label="Artist Spotify Profile"
            type="url"
            placeholder="https://open.spotify.com/artist/..."
            value={formData?.artistSpotify}
            onChange={(e) => handleInputChange('artistSpotify', e?.target?.value)}
            error={errors?.artistSpotify}
            disabled={!isEditing}
            className="mb-4"
          />
          
          <Input
            label="Artist Apple Music Profile"
            type="url"
            placeholder="https://music.apple.com/artist/..."
            value={formData?.artistAppleMusic}
            onChange={(e) => handleInputChange('artistAppleMusic', e?.target?.value)}
            error={errors?.artistAppleMusic}
            disabled={!isEditing}
            className="mb-4"
          />
          
          <Input
            label="Artist YouTube Music Channel"
            type="url"
            placeholder="https://music.youtube.com/channel/..."
            value={formData?.artistYouTubeMusic}
            onChange={(e) => handleInputChange('artistYouTubeMusic', e?.target?.value)}
            error={errors?.artistYouTubeMusic}
            disabled={!isEditing}
            className="mb-4"
          />
          
          <Input
            label="Artist Tidal Profile"
            type="url"
            placeholder="https://tidal.com/browse/artist/..."
            value={formData?.artistTidal}
            onChange={(e) => handleInputChange('artistTidal', e?.target?.value)}
            error={errors?.artistTidal}
            disabled={!isEditing}
            className="mb-4"
          />
        </div>
      </div>
      {/* Status Indicator - Updated to include new social media entries */}
      <div className="mt-6 p-4 bg-muted rounded-lg">
        <div className="flex items-center space-x-2">
          <Icon 
            name={formData?.name && formData?.email && formData?.artist_type ? "CheckCircle" : "AlertCircle"} 
            size={16} 
            className={formData?.name && formData?.email && formData?.artist_type ? "text-success" : "text-warning"}
          />
          <span className="text-sm font-medium text-text-primary">
            Profile Status: {formData?.name && formData?.email && formData?.artist_type ? "Complete" : "Incomplete"}
          </span>
        </div>
        {(!formData?.name || !formData?.email || !formData?.artist_type) && (
          <p className="text-sm text-text-secondary mt-1">
            Please complete all required fields including artist type to activate your profile.
          </p>
        )}
        {formData?.artist_type && (
          <div className="mt-2 flex items-center space-x-2">
            <Icon name="Palette" size={14} className="text-primary" />
            <span className="text-sm text-text-primary font-medium">
              Artist Type: {artistTypeOptions?.find(opt => opt?.value === formData?.artist_type)?.label || formData?.artist_type}
            </span>
          </div>
        )}
        
        {/* Highlight Color Status - Fixed preview not dimmed */}
        {formData?.highlightColor && (
          <div className="mt-2 flex items-center space-x-2">
            <div 
              className="w-4 h-4 rounded border border-border"
              style={{ backgroundColor: formData?.highlightColor }}
            />
            <span className="text-sm text-text-primary font-medium">
              Mobile Theme Color: {formData?.highlightColor}
            </span>
          </div>
        )}

        {/* Background Color Status - NEW */}
        {formData?.backgroundColor && (
          <div className="mt-2 flex items-center space-x-2">
            <div 
              className="w-4 h-4 rounded border border-border"
              style={{ backgroundColor: formData?.backgroundColor }}
            />
            <span className="text-sm text-text-primary font-medium">
              Mobile Background Color: {formData?.backgroundColor}
            </span>
          </div>
        )}

        {/* Artist Logo Status */}
        {formData?.artist_logo_url && (
          <div className="mt-2 flex items-center space-x-2">
            <Icon name="Image" size={14} className="text-accent" />
            <span className="text-sm text-text-primary font-medium">
              Artist Logo: Uploaded
            </span>
          </div>
        )}
        
        {/* Social Media Status - Updated to include SoundCloud and Snapchat */}
        {(formData?.instagram || formData?.twitter || formData?.facebook || formData?.soundcloud || formData?.snapchat || formData?.imdb || formData?.linkedin) && (
          <div className="mt-2 flex items-center space-x-2">
            <Icon name="Users" size={14} className="text-accent" />
            <span className="text-sm text-text-primary font-medium">
              Social Media: {[formData?.instagram, formData?.twitter, formData?.facebook, formData?.soundcloud, formData?.snapchat, formData?.imdb, formData?.linkedin]?.filter(Boolean)?.length} platforms linked
            </span>
          </div>
        )}
        
        {/* Artist Music URLs Status */}
        {(formData?.artistSpotify || formData?.artistAppleMusic || formData?.artistYouTubeMusic || formData?.artistTidal) && (
          <div className="mt-2 flex items-center space-x-2">
            <Icon name="Music" size={14} className="text-accent" />
            <span className="text-sm text-text-primary font-medium">
              Music Platforms: {[formData?.artistSpotify, formData?.artistAppleMusic, formData?.artistYouTubeMusic, formData?.artistTidal]?.filter(Boolean)?.length} linked
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProfileSection;